import { CircularProgress, Icon } from '@mui/material';
import { FormEvent, FunctionComponent, useRef, useState } from 'react';

import { UnlockSerieResponse, UnlockedSerie } from '../../../shared/api/request';

interface UnlockSerieCardProps {
  onUnlockSerie: (code: string) => Promise<UnlockSerieResponse>;
}

type Status = 'idle' | 'loading' | 'error' | 'success';

const formatSuccess = (series: UnlockedSerie[]) => {
  if (series.length === 0) return 'Code validé ! Vos contenus sont maintenant disponibles.';
  if (series.length === 1) return `Code validé ! « ${series[0].name} » est maintenant dans votre bibliothèque.`;
  return `Code validé ! ${series.length} séries ont été ajoutées à votre bibliothèque.`;
};

const UnlockSerieCard: FunctionComponent<UnlockSerieCardProps> = ({ onUnlockSerie }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [feedback, setFeedback] = useState('');

  const isLoading = status === 'loading';

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;

    const value = code.trim().toUpperCase();
    if (!value) {
      setStatus('error');
      setFeedback('Saisissez votre code série pour continuer.');
      inputRef.current?.focus();
      return;
    }

    setStatus('loading');
    setFeedback('');
    const res = await onUnlockSerie(value);

    if (res.isOk) {
      setStatus('success');
      setFeedback(formatSuccess(res.series));
      setCode('');
    } else {
      setStatus('error');
      setFeedback(res.msg || "Ce code n'est pas valide.");
      inputRef.current?.focus();
    }
  };

  return (
    <section className="nbk-card nbk-unlock" aria-labelledby="nbk-unlock-title">
      <div className="nbk-card__icon" aria-hidden="true">
        <Icon>vpn_key</Icon>
      </div>
      <h2 id="nbk-unlock-title" className="nbk-card__title">
        Vous avez un code série ?
      </h2>
      <p className="nbk-card__text">Saisissez-le ci-dessous : la série sera débloquée et ajoutée à votre bibliothèque.</p>

      <form className="nbk-unlock__form" onSubmit={onSubmit} noValidate>
        <label htmlFor="nbk-unlock-code" className="nbk-visually-hidden">
          Code série
        </label>
        <input
          ref={inputRef}
          id="nbk-unlock-code"
          className={`nbk-unlock__input${status === 'error' ? ' nbk-unlock__input--error' : ''}`}
          value={code}
          onChange={e => {
            setCode(e.target.value.toUpperCase());
            if (status === 'error' || status === 'success') setStatus('idle');
          }}
          placeholder="EX : ABCD1234"
          maxLength={25}
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          disabled={isLoading}
          aria-invalid={status === 'error'}
          aria-describedby="nbk-unlock-feedback"
        />
        <button type="submit" className="nbk-btn nbk-btn--primary nbk-unlock__submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={18} color="inherit" aria-hidden="true" />
              Vérification…
            </>
          ) : (
            'Débloquer'
          )}
        </button>
      </form>

      <div id="nbk-unlock-feedback" className="nbk-unlock__feedback" aria-live="polite">
        {status === 'error' && feedback ? (
          <p className="nbk-feedback nbk-feedback--error" role="alert">
            <Icon aria-hidden="true">error_outline</Icon>
            <span>{feedback}</span>
          </p>
        ) : null}
        {status === 'success' && feedback ? (
          <p className="nbk-feedback nbk-feedback--success">
            <Icon aria-hidden="true">check_circle</Icon>
            <span>{feedback}</span>
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default UnlockSerieCard;
